import React from "react";
import { SprayIcon } from "@/components/icons/SprayIcon";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  User as UserIcon,
  LogOut,
  ShieldCheck,
  Wrench,
  MessageSquare,
  Gift,
  Calendar,
  CalendarCheck,
  Copy,
  Settings,
  Languages,
  Palette,
  Sun,
  Moon,
  Monitor,
  Check,
  Fish,
  Bell,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import UserAvatar from "@/components/shared/Avatar";
import { useUserStore } from "@/stores/userStore";
import { checkInService } from "@/services/checkInService";
import { useLanguage } from "@/hooks/useLanguage";
import { ThemeSetting, ResolvedTheme } from "@/hooks/useTheme";
import { usePoints } from "@/hooks/usePoints";

interface UserMenuProps {
  user: {
    name: string;
    account_id?: string;
    avatar_url?: string | null;
    points?: number;
    checked_in?: boolean;
  };
  unreadCount: number;
  themeSetting: ThemeSetting;
  setTheme: (theme: ThemeSetting) => void;
}

export function UserMenu({
  user,
  unreadCount,
  themeSetting,
  setTheme,
}: UserMenuProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logout = useUserStore((state) => state.logout);
  const [checkedIn, setCheckedIn] = React.useState(user.checked_in ?? false);

  const {
    currentLanguage,
    changeLanguage,
    supportedLanguages,
    getLanguageConfig,
  } = useLanguage();

  // 使用 usePoints hook 获取实时积分余额
  const { data: pointsData } = usePoints();
  const currentPoints = pointsData?.balance ?? user.points ?? 0;

  React.useEffect(() => {
    setCheckedIn(user.checked_in ?? false);
  }, [user.checked_in]);

  const handleCopyId = React.useCallback(() => {
    if (!user.account_id) return;
    navigator.clipboard
      .writeText(user.account_id)
      .then(() => toast.success(t("nav.idCopied")))
      .catch(() => toast.error(t("nav.copyFailed")));
  }, [user.account_id, t]);

  const handleCheckIn = React.useCallback(async () => {
    if (checkedIn) return;
    try {
      const data = await checkInService.checkIn();
      setCheckedIn(true);
      toast.success(
        data.points !== undefined
          ? t("nav.checkInSuccess", { points: data.points })
          : t("nav.checkInSuccessSimple"),
      );
    } catch (error) {
      if (error instanceof Error && error.message.includes("已签到")) {
        setCheckedIn(true);
      }
    }
  }, [checkedIn, t]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
      toast.success(t("nav.logoutSuccess", "已成功退出登录"));
    } catch (error) {
      console.error(
        "API logout failed, falling back to form submission:",
        error,
      );
      // Fallback: Use manual form submission for Devise session clearing
      const csrfToken =
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
          ?.content || "";
      const form = document.createElement("form");
      form.method = "POST";
      form.action = "/users/sign_out";

      const methodInput = document.createElement("input");
      methodInput.type = "hidden";
      methodInput.name = "_method";
      methodInput.value = "delete";
      form.appendChild(methodInput);

      const csrfInput = document.createElement("input");
      csrfInput.type = "hidden";
      csrfInput.name = "authenticity_token";
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);

      document.body.appendChild(form);
      form.submit();
    }
  };

  const menuIconClass = "size-4";
  const menuLinkClass = "flex text-sm font-medium w-full items-center gap-2.5";
  const menuItemClass = "rounded-md h-9";

  return (
    <div className="flex items-center gap-2">
      {/* 邀请有礼 */}
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" asChild>
            <Link to="/invitations">
              <Gift className="size-4" />
            </Link>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("nav.invitations")}</TooltipContent>
      </Tooltip>

      {/* 通知 */}
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" asChild>
            <Link to="/notifications">
              <span className="relative">
                <Bell className="size-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-destructive ring ring-background" />
                )}
              </span>
            </Link>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("nav.notifications")}</TooltipContent>
      </Tooltip>

      {/* 用户下拉菜单 */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="rounded-md">
            <UserAvatar
              name={user.name}
              src={user.avatar_url}
              size={32}
              className="h-8 w-8 rounded-md"
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-72 px-3 pb-3 border-border"
        >
          <div className="px-3 py-4 flex items-center gap-3">
            <UserAvatar
              name={user.name}
              src={user.avatar_url}
              size={36}
              className="h-9 w-9 rounded-md"
            />
            <div className="flex flex-col flex-1 min-w-0">
              <span className="font-semibold text-sm truncate">
                {user.name}
              </span>
              {user.account_id && (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-muted-foreground truncate">
                    ID: {user.account_id}
                  </span>
                  <button
                    onClick={handleCopyId}
                    className="text-muted-foreground/70 hover:text-muted-foreground"
                  >
                    <Copy className="size-3" />
                  </button>
                </div>
              )}
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "bg-muted",
                    checkedIn && "pointer-events-none text-muted-foreground",
                  )}
                  onClick={handleCheckIn}
                  disabled={checkedIn}
                >
                  {checkedIn ? (
                    <CalendarCheck className="size-5" />
                  ) : (
                    <Calendar className="size-5" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {checkedIn ? t("nav.checkedIn") : t("nav.checkIn")}
              </TooltipContent>
            </Tooltip>
          </div>

          <DropdownMenuSeparator className="bg-border -mx-3" />

          {/* 积分余额 - 独立区块 */}
          <div className="px-1 py-1.5">
            <Link
              to="/points"
              className="flex items-center gap-2.5 px-2 py-2.5 rounded-md border border-border bg-muted/30 hover:bg-muted/60 transition-colors"
            >
              <SprayIcon className="size-4 text-primary" />
              <span className="flex-1 text-sm font-medium">
                {t("nav.points", "水花")}
              </span>
              <span className="text-sm font-semibold tabular-nums text-primary">
                {currentPoints.toLocaleString()}
              </span>
            </Link>
          </div>

          <DropdownMenuSeparator className="bg-border -mx-3" />

          <DropdownMenuItem className={menuItemClass} asChild>
            <Link to="/profile" className={menuLinkClass}>
              <UserIcon
                className={cn(menuIconClass, "text-muted-foreground")}
              />
              {t("nav.profile")}
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem className={menuItemClass} asChild>
            <Link to="/tanks" className={menuLinkClass}>
              <Fish className={cn(menuIconClass, "text-muted-foreground")} />
              {t("nav.tanks")}
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem className={menuItemClass} asChild>
            <Link to="/toolbox" className={menuLinkClass}>
              <Wrench className={cn(menuIconClass, "text-muted-foreground")} />
              {t("nav.toolbox")}
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem className={menuItemClass} asChild>
            <Link to="/security" className={menuLinkClass}>
              <ShieldCheck
                className={cn(menuIconClass, "text-muted-foreground")}
              />
              {t("nav.security")}
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem className={menuItemClass} asChild>
            <Link to="/feedback" className={menuLinkClass}>
              <MessageSquare
                className={cn(menuIconClass, "text-muted-foreground")}
              />
              {t("nav.feedback")}
            </Link>
          </DropdownMenuItem>

          {/* 设置子菜单 */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className={cn(menuItemClass, "px-2")}>
              <Settings
                className={cn(menuIconClass, "text-muted-foreground mr-2.5")}
              />
              <span className="text-sm font-medium">{t("nav.settings")}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="min-w-[160px] border-border">
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="px-2 py-1.5">
                  <Languages
                    className={cn(menuIconClass, "text-muted-foreground mr-2")}
                  />
                  <span className="text-sm">{t("nav.language")}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="min-w-[140px] border-border">
                  {supportedLanguages.map((lang) => {
                    const config = getLanguageConfig(lang);
                    return (
                      <DropdownMenuItem
                        key={lang}
                        onClick={() => changeLanguage(lang)}
                        className="flex items-center gap-2"
                      >
                        <span>{config.flag}</span>
                        <span className="flex-1 text-sm">{config.label}</span>
                        {currentLanguage === lang && (
                          <Check className="size-3.5" />
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuSubContent>
              </DropdownMenuSub>

              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="px-2 py-1.5">
                  <Palette
                    className={cn(menuIconClass, "text-muted-foreground mr-2")}
                  />
                  <span className="text-sm">{t("nav.theme")}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="min-w-[120px] border-border">
                  <DropdownMenuItem
                    onClick={() => setTheme("light")}
                    className="flex items-center gap-2"
                  >
                    <Sun
                      className={cn(menuIconClass, "text-muted-foreground")}
                    />
                    <span className="flex-1 text-sm">{t("nav.lightMode")}</span>
                    {themeSetting === "light" && <Check className="size-3.5" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setTheme("dark")}
                    className="flex items-center gap-2"
                  >
                    <Moon
                      className={cn(menuIconClass, "text-muted-foreground")}
                    />
                    <span className="flex-1 text-sm">{t("nav.darkMode")}</span>
                    {themeSetting === "dark" && <Check className="size-3.5" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setTheme("system")}
                    className="flex items-center gap-2"
                  >
                    <Monitor
                      className={cn(menuIconClass, "text-muted-foreground")}
                    />
                    <span className="flex-1 text-sm">
                      {t("nav.systemMode")}
                    </span>
                    {themeSetting === "system" && (
                      <Check className="size-3.5" />
                    )}
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuItem
            onClick={handleLogout}
            className={cn(
              menuItemClass,
              "group text-destructive hover:bg-destructive hover:text-white focus:bg-destructive focus:text-white cursor-pointer",
            )}
          >
            <div className="flex w-full items-center gap-2.5">
              <LogOut
                className={cn(
                  menuIconClass,
                  "text-destructive group-hover:text-white group-focus:text-white",
                )}
              />
              <span className="text-sm font-medium">{t("nav.logout")}</span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
